import { type RefObject, useCallback, useEffect, useRef } from "react";
import type { CropperRef } from "react-advanced-cropper";

/**
 * Distance (in px) from a boundary edge at which auto-panning kicks in while
 * dragging the crop selection.
 */
const EDGE_THRESHOLD = 36;
/** Maximum pan distance (in screen px) applied per animation frame. */
const MAX_STEP = 16;

/**
 * Bounding size of the image once the current rotation is applied. Mirrors the
 * library's `getTransformedImageSize` so panning stops exactly at the image
 * borders regardless of rotation.
 */
const getTransformedImageSize = (
	imageSize: { width: number; height: number },
	rotate: number,
) => {
	const angle = (rotate * Math.PI) / 180;
	const sin = Math.abs(Math.sin(angle));
	const cos = Math.abs(Math.cos(angle));
	return {
		width: imageSize.width * cos + imageSize.height * sin,
		height: imageSize.width * sin + imageSize.height * cos,
	};
};

/**
 * Matches the move/drag area of either stencil shape (rectangle or circle), but
 * not the resize handles — so auto-pan only triggers when repositioning the
 * selection, not when resizing it or dragging the image.
 */
const STENCIL_DRAG_SELECTOR = '[class*="stencil__draggable-area"]';

/**
 * Lets the user move the crop selection across the entire image while zoomed in.
 *
 * `react-advanced-cropper` hard-clamps the stencil to the current visible area,
 * so once the image is zoomed past the boundary the selection can no longer be
 * dragged to image regions outside the view (this also produces a confusing
 * X/Y asymmetry depending on the image aspect ratio). To restore the expected
 * behaviour we auto-pan the image — exactly the gap the reported bug describes —
 * whenever the pointer approaches a boundary edge during a stencil drag, until
 * the corresponding image border is reached.
 *
 * Returns a callback ref to attach to the element wrapping the `<Cropper>` (it
 * is used both for pointer tracking and to measure the boundary rect).
 */
export const useCropperEdgePan = (cropperRef: RefObject<CropperRef | null>) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const pointerRef = useRef<{ x: number; y: number } | null>(null);
	const draggingStencilRef = useRef(false);
	const rafRef = useRef<number | null>(null);

	const tick = useCallback(() => {
		rafRef.current = null;

		const cropper = cropperRef.current;
		const container = containerRef.current;
		const pointer = pointerRef.current;
		if (!cropper || !container || !pointer || !draggingStencilRef.current)
			return;

		const schedule = () => {
			rafRef.current = requestAnimationFrame(tick);
		};

		const visibleArea = cropper.getVisibleArea();
		const coordinates = cropper.getCoordinates();
		const state = cropper.getState();
		if (!visibleArea || !coordinates || !state) {
			schedule();
			return;
		}

		const rect = container.getBoundingClientRect();
		if (!rect.width || !rect.height) {
			schedule();
			return;
		}

		// Visible area shares the boundary's aspect ratio, so the screen→image
		// scale is uniform on both axes.
		const scale = visibleArea.width / rect.width;

		const proximity = (distance: number) =>
			Math.min(Math.max((EDGE_THRESHOLD - distance) / EDGE_THRESHOLD, 0), 1);

		let dxScreen = 0;
		let dyScreen = 0;

		const leftDist = pointer.x - rect.left;
		const rightDist = rect.right - pointer.x;
		const topDist = pointer.y - rect.top;
		const bottomDist = rect.bottom - pointer.y;

		if (leftDist < EDGE_THRESHOLD) dxScreen = -proximity(leftDist) * MAX_STEP;
		else if (rightDist < EDGE_THRESHOLD)
			dxScreen = proximity(rightDist) * MAX_STEP;

		if (topDist < EDGE_THRESHOLD) dyScreen = -proximity(topDist) * MAX_STEP;
		else if (bottomDist < EDGE_THRESHOLD)
			dyScreen = proximity(bottomDist) * MAX_STEP;

		if (dxScreen || dyScreen) {
			const imageSize = getTransformedImageSize(
				state.imageSize,
				state.transforms?.rotate ?? 0,
			);

			let dx = dxScreen * scale;
			let dy = dyScreen * scale;

			// Stop panning once the image border on that side is fully reached: there
			// is no point scrolling further into empty space beyond the image.
			if (dx < 0) dx = -Math.min(-dx, Math.max(visibleArea.left, 0));
			else if (dx > 0)
				dx = Math.min(
					dx,
					Math.max(imageSize.width - (visibleArea.left + visibleArea.width), 0),
				);

			if (dy < 0) dy = -Math.min(-dy, Math.max(visibleArea.top, 0));
			else if (dy > 0)
				dy = Math.min(
					dy,
					Math.max(
						imageSize.height - (visibleArea.top + visibleArea.height),
						0,
					),
				);

			if (dx || dy) {
				cropper.moveImage(dx, dy, { transitions: false });
			}
		}

		schedule();
	}, [cropperRef]);

	const handlePointerDown = useCallback(
		(e: PointerEvent) => {
			const target = e.target as Element | null;
			// Only react to a drag that started on the selection's move area.
			if (!target?.closest(STENCIL_DRAG_SELECTOR)) return;
			draggingStencilRef.current = true;
			pointerRef.current = { x: e.clientX, y: e.clientY };
			if (rafRef.current === null) {
				rafRef.current = requestAnimationFrame(tick);
			}
		},
		[tick],
	);

	// Track the pointer globally and stop panning once the drag ends.
	useEffect(() => {
		const handlePointerMove = (e: PointerEvent) => {
			if (pointerRef.current) {
				pointerRef.current = { x: e.clientX, y: e.clientY };
			}
		};
		const stop = () => {
			draggingStencilRef.current = false;
			pointerRef.current = null;
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};

		window.addEventListener("pointermove", handlePointerMove);
		window.addEventListener("pointerup", stop);
		window.addEventListener("pointercancel", stop);

		return () => {
			window.removeEventListener("pointermove", handlePointerMove);
			window.removeEventListener("pointerup", stop);
			window.removeEventListener("pointercancel", stop);
			stop();
		};
	}, []);

	// Callback ref so listeners attach exactly when the cropper wrapper mounts
	// (it is rendered asynchronously, after the image is ready).
	return useCallback(
		(node: HTMLDivElement | null) => {
			if (containerRef.current) {
				containerRef.current.removeEventListener(
					"pointerdown",
					handlePointerDown,
				);
			}
			containerRef.current = node;
			if (node) {
				node.addEventListener("pointerdown", handlePointerDown);
			}
		},
		[handlePointerDown],
	);
};
